import Big from 'big.js';
import {DEFAULT_USD_PRICE_SCALE} from '@/const/data-config';
import {minus, numUtil, plus, pow10, sum, toFixed} from '@/helper/num';
import {Assets} from '@/types/assets';
import {AssetDigital, AssetEntity, CurrencyEntity, PriceEntity, PricesCache} from '@/types';
import {calcValueBalanceUtils} from '@/helper/calc-value-balance';

const AssetUnScale = false;

export function calcTotalAssets(
  assets: AssetEntity[],
  indexPricesCache: PricesCache,
  currenciesCurrencyCache: Map<string, CurrencyEntity>,
  fiat: string,
  assetUnScale: boolean = AssetUnScale,
  calcBonus: boolean = false,
  rm: 0 | 3 = 0
): Assets {
  if (!indexPricesCache.has('.BTC')) {
    return null;
  }
  const btcCurrency = currenciesCurrencyCache.get('BTC');
  if (!btcCurrency) {
    return null;
  }

  const usdCurrency = currenciesCurrencyCache.get('USD');
  if (!usdCurrency) {
    return null;
  }

  const usdtIndexPrice = indexPricesCache.get('.USDT');
  if (!usdtIndexPrice) {
    return null;
  }

  const res = calcTotalAssetBase(assets, indexPricesCache, currenciesCurrencyCache, fiat, assetUnScale, false, rm);

  if (!calcBonus) {
    return res;
  }

  const {
    btcEv: bonusBtcEv,
    btc: bonusBtc,
    usdEv: bonusUsdEv,
    usd: bonusUsd,
    usdtEv: bonusUsdtEv,
    usdt: bonusUsdt,
    money: bonusMoney,
  } = calcTotalAssetBase(assets, indexPricesCache, currenciesCurrencyCache, fiat, assetUnScale, true, rm);

  return {
    ...res,
    bonusBtcEv,
    bonusBtc,
    bonusUsdEv,
    bonusUsd,
    bonusUsdtEv,
    bonusUsdt,
    bonusMoney,
  };
}

function calcTotalAssetBase(
  assets: AssetEntity[],
  indexPricesCache: PricesCache,
  currenciesCurrencyCache: Map<string, CurrencyEntity>,
  fiat: string,
  assetUnScale: boolean = AssetUnScale,
  isBonus = false,
  rm: 0 | 3 = 0
) {
  if (!indexPricesCache.has('.BTC')) {
    return null;
  }
  const btcCurrency = currenciesCurrencyCache.get('BTC');

  const usdCurrency = currenciesCurrencyCache.get('USD');

  const [btcValuePrecision, btcValueScale] = convertCurrencyConfig(btcCurrency);
  const [usdValuePrecision, usdValueScale] = convertCurrencyConfig(usdCurrency);

  const {priceEp: btcIndexPriceEp} = indexPricesCache.get('.BTC');

  const usdtIndexPrice = indexPricesCache.get('.USDT');

  const btcAssets = assets.filter((o) => o.currency === 'BTC');
  const usdAssets = assets.filter((o) => o.currency === 'USD');
  const othersAssets = assets.filter((o) => ['BTC', 'USD'].indexOf(o.currency) < 0);

  const btcAssetVals = btcAssets.map((o) => (isBonus ? o.bonusBalanceEv : o.valueEv));
  const usdAssetVals = usdAssets.map((o) => (isBonus ? o.bonusBalanceEv : o.valueEv));

  const btcAssetsEv = sum(...btcAssetVals);
  const usdAssetsEv = sum(...usdAssetVals);
  const othersAssetsEv = othersAssets.reduce(
    (p: string, asset) =>
      plus(p, calcAssetUsdEv(asset, indexPricesCache, currenciesCurrencyCache, usdValueScale, assetUnScale, isBonus)),
    '0'
  );

  const btcEv = calcBtcEv(
    btcAssetsEv,
    usdAssetsEv,
    othersAssetsEv,
    btcIndexPriceEp,
    btcValueScale,
    usdValueScale,
    assetUnScale
  );
  const usdEv = calcUsdEv(
    btcAssetsEv,
    usdAssetsEv,
    othersAssetsEv,
    btcIndexPriceEp,
    btcValueScale,
    usdValueScale,
    assetUnScale
  );
  const fiatRate = indexPricesCache.get('.' + fiat);
  const moneyEv = calcMoneyEv(fiatRate, usdEv);
  const usdtEv = Big(usdEv).times(usdtIndexPrice.priceFactor).div(usdtIndexPrice.priceEp).toNumber();

  return {
    btcEv,
    btc: convertValue(btcEv, btcValuePrecision, btcValueScale, rm),
    usdEv,
    usd: convertValue(usdEv, usdValuePrecision, usdValueScale, rm),
    usdtEv,
    usdt: convertValue(usdtEv, usdValuePrecision, usdValueScale, rm),
    money: convertValue(moneyEv, usdCurrency.valuePrecision, usdCurrency.valueScale, rm),
  };
}

function calcBtcEv(
  btcAssetsEv: AssetDigital,
  usdAssetsEv: AssetDigital,
  othersAssetsEv: AssetDigital,
  btcIndexPriceEp: AssetDigital,
  btcValueScale: number,
  usdValueScale: number,
  assetUnScale: boolean = AssetUnScale
): number {
  return Math.floor(
    Number(
      Big(usdAssetsEv)
        .plus(othersAssetsEv)
        .times(
          pow10(
            assetUnScale
              ? DEFAULT_USD_PRICE_SCALE
              : Big(btcValueScale).plus(DEFAULT_USD_PRICE_SCALE).minus(usdValueScale)
          )
        )
        .div(btcIndexPriceEp)
        .plus(btcAssetsEv)
        .times(pow10(assetUnScale ? btcValueScale : 0))
        .valueOf()
    )
  );
}

function calcUsdEv(
  btcAssetsEv: AssetDigital,
  usdAssetsEv: AssetDigital,
  othersAssetsEv: AssetDigital,
  btcIndexPriceEp: AssetDigital,
  btcValueScale: number,
  usdValueScale: number,
  assetUnScale: boolean = AssetUnScale
) {
  return Math.floor(
    Number(
      Big(btcAssetsEv)
        .times(btcIndexPriceEp)
        .times(
          pow10(
            assetUnScale
              ? -DEFAULT_USD_PRICE_SCALE
              : Big(usdValueScale).minus(DEFAULT_USD_PRICE_SCALE).minus(btcValueScale)
          )
        )
        .plus(usdAssetsEv)
        .plus(othersAssetsEv)
        .times(pow10(assetUnScale ? usdValueScale : 0))
        .valueOf()
    )
  );
}

function calcAssetUsdEv(
  asset: AssetEntity,
  indexPricesCache: PricesCache,
  currenciesCurrencyCache: Map<string, CurrencyEntity>,
  usdValueScale: number,
  assetUnScale: boolean = AssetUnScale,
  isBonus = false
): AssetDigital {
  const {currency} = asset;
  const currentCurrency = currenciesCurrencyCache.get(currency);
  if (!currentCurrency) {
    return 0;
  }

  const currentIndexPrice = indexPricesCache.get('.' + currency);
  if (!currentIndexPrice) {
    return 0;
  }

  const scale = assetUnScale
    ? -DEFAULT_USD_PRICE_SCALE
    : minus(usdValueScale, plus(DEFAULT_USD_PRICE_SCALE, currentCurrency.valueScale));

  return Big(isBonus ? asset.bonusBalanceEv : asset.valueEv)
    .times(currentIndexPrice.priceEp)
    .times(pow10(scale))
    .valueOf();
}

/**
 *
 * @param currencyInfo
 * @returns [precision, scale, factor]
 */
function convertCurrencyConfig(currencyInfo): [precision: number, scale: number, factor: number] {
  return [currencyInfo.valuePrecision, currencyInfo.valueScale, currencyInfo.valueFactor];
}

function convertValue(valueEv, precision, scale, rm: 0 | 3 = 0): string {
  const formatter = rm === 3 ? numUtil.ceil : numUtil.floor;
  return numUtil.delimit(formatter(valueEv, precision, scale));
}

function calcMoneyEv(fiatRate: PriceEntity, usdEv) {
  if (!fiatRate) {
    return 0;
  }
  return Number(Big(usdEv).times(fiatRate.priceFactor).div(fiatRate.priceEp));
}

export function mergeAssets([
  currenciesCache,
  fiat,
  indexPricesCache,
  // 各类资产
  ...rawAssets
]: [Map<string, CurrencyEntity>, string, Map<string, PriceEntity>, ...Assets[]]): Assets {
  const btcCurrency = currenciesCache.get('BTC');
  if (!btcCurrency) {
    return null;
  }

  const usdCurrency = currenciesCache.get('USD');
  if (!usdCurrency) {
    return null;
  }

  const assets = (rawAssets || []).filter(Boolean);

  const res = mergeAssetsBase(fiat, indexPricesCache, usdCurrency, btcCurrency, assets);
  const {
    btcEv: bonusBtcEv,
    btc: bonusBtc,
    usdEv: bonusUsdEv,
    usd: bonusUsd,
    usdtEv: bonusUsdtEv,
    usdt: bonusUsdt,
    money: bonusMoney,
  } = mergeAssetsBase(fiat, indexPricesCache, usdCurrency, btcCurrency, assets, true);

  return {
    ...res,
    bonusBtcEv,
    bonusBtc,
    bonusUsdEv,
    bonusUsd,
    bonusUsdtEv,
    bonusUsdt,
    bonusMoney,
  };
}

function mergeAssetsBase(
  fiat: string,
  indexPricesCache: Map<string, PriceEntity>,
  usdCurrency,
  btcCurrency,
  assets: Assets[],
  isBonus = false
) {
  const {valueScale, valueFactor} = usdCurrency;

  const USD_PRECISION = 2;
  const MONEY_PRECISION = 2;
  const btcEv = Number(toFixed(sum(...assets.map((a) => (isBonus ? a.bonusBtcEv || 0 : a.btcEv))), 0, 0));
  const usdEv = Number(toFixed(sum(...assets.map((a) => (isBonus ? a.bonusUsdEv || 0 : a.usdEv))), 0, 0));
  const fiatRate = indexPricesCache.get('.' + fiat);
  const moneyEv = calcMoneyEv(fiatRate, usdEv);
  const money = numUtil.delimit(numUtil.floor(moneyEv, MONEY_PRECISION, valueScale));
  const usdtRate = indexPricesCache.get('.USDT');

  const usdtEv = calcValueBalanceUtils.getBalanceEv(
    usdEv,
    usdtRate.priceEp,
    valueFactor,
    usdtRate.priceFactor,
    valueFactor
  );

  return {
    btcEv,
    btc: numUtil.floor(btcEv, btcCurrency.valuePrecision, btcCurrency.valueScale),
    usdEv,
    usd: numUtil.delimit(numUtil.floor(usdEv, USD_PRECISION, valueScale)),
    money,
    usdtEv,
    usdt: numUtil.delimit(numUtil.floor(usdtEv, USD_PRECISION, valueScale)),
  };
}
